import { makeStyles } from "@material-ui/core";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  buildForecastSummaryApiFilters,
  buildForecastSummaryDownloadFilters,
  getApiFilterKey,
  getGlobalPeriodColumnKeys,
} from "./waterfall-eac/waterfallEacFilterUtils";
import { isBudgetVersion } from "./waterfall-eac/filterPopoverUtils";
import {
  getForecastSummaryMonthlyAPI,
  downloadForecastSummaryMonthlyAPI,
  getVersionsAPI,
} from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";
import ForecastSummaryTable from "./forecast-monthly/ForecastSummaryTable";

import {
  TH_BG_BAND,
} from "../reportTableConfig";

const TAB_ACTIVE = "#4F46E5";

/* ── Tabs ────────────────────────────────────────────────────── */
const TABS = [{ key: "forecast_summary", label: "Forecast Summary" }];

const MONTH_LABEL = {
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
  total: "Total",
};

/* Global filters exposed for Forecast Monthly Summary */
const FMS_FILTERS = [
  "year",
  "monthName",
  "quarter",
  "orgId",
  "contractType",
  "contractId",
  "clinLvl2",
  "businessType",
  "awardType",
  "competitionType",
  "bu",
  "popEndDate",
  "projectId",
];

function mapForecastRow(r) {
  return {
    monthKey: r.month,
    month: MONTH_LABEL[r.month] ?? r.month,
    type: r.type ?? "",
    days: r.workingDays,
    revenue: r.contractRevenue,
    directLabor: r.directLabor,
    subcontract: r.subcontractor,
    odcAmt: r.odcs,
    tdc: r.totalDirectCost,
    grossMargin: r.grossMargin,
    gmPct: r.grossMarginPct != null ? r.grossMarginPct * 100 : null,
    indirectCost: r.indirectCost,
    totalCost: r.totalCost,
    netProfit: r.netProfit,
    nmPct: r.netMarginPct != null ? r.netMarginPct * 100 : null,
  };
}

/* ── API response → table row transform ── */
function transformForecastSummary(data) {
  if (!data || !Array.isArray(data.rows)) return null;
  const monthRows = [];
  let totalRow = null;
  data.rows.forEach((r) => {
    const mapped = mapForecastRow(r);
    if (r.month === "total") totalRow = mapped;
    else monthRows.push(mapped);
  });
  return {
    monthRows,
    totalRow,
    fiscalYear: data.fiscalYear,
    actualThroughMonth: data.actualThroughMonth,
    version: data.version,
    versionName: data.version?.name ?? "Live EAC",
  };
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PL_PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: "#4F46E5",
    background: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  /* ── horizontal filter bar (matches Scenario YoY) ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: "1px solid #4F46E5", borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: "#4F46E5",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: "#4F46E5", borderColor: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: "1px solid #E5E7EB",
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: "#4F46E5" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  /* ── tab bar ── */
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    background: "none",
    fontFamily: REPORT_FONT,
    transition: "color 0.15s, box-shadow 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 600,
    boxShadow: `inset 0 -2px 0 0 ${TAB_ACTIVE}`,
  },

  headerContainer: { margin: "20px 0 0", padding: "0 16px" },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "0 16px", overflowY: "auto", minWidth: 0 },

  /* ── version picker pill ── */
  filterRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 4,
    gap: 10,
  },
  filterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: REPORT_FONT,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  filterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 120,
    height: "100%",
    fontFamily: REPORT_FONT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function GlobalFilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Horizontal filter bar (matches Scenario YoY)                  */
/* ══════════════════════════════════════════════════════════════ */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key));
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

export default function ForecastMonthlySummary() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);

  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = FMS_FILTERS;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  const apiFilterKey = useMemo(
    () => getApiFilterKey(effectiveFilters, buildForecastSummaryApiFilters),
    [effectiveFilters],
  );

  /* Fetch available versions when the year filter changes */
  useEffect(() => {
    getVersionsAPI(appliedFilters.year).then((res) => {
      const list = (Array.isArray(res?.data) ? res.data : []).filter(
        (v) => !isBudgetVersion(v),
      );
      setVersions(list);
      setVersion((prev) => {
        if (prev && list.some((v) => v.key === prev)) return prev;
        const live = list.find((v) => v.key === "liveEac");
        return live?.key ?? list[0]?.key ?? "";
      });
    });
  }, [appliedFilters.year]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    getForecastSummaryMonthlyAPI(
      buildForecastSummaryApiFilters(effectiveFilters),
      version,
    )
      .then((res) => setData(transformForecastSummary(res?.data ?? res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFilterKey, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodMonthKeys = useMemo(
    () => getGlobalPeriodColumnKeys(appliedFilters, "Month"),
    [appliedFilters],
  );

  const rows = useMemo(() => {
    const monthRows = data?.monthRows ?? [];
    if (!periodMonthKeys?.length) return monthRows;
    const allowed = new Set(periodMonthKeys);
    return monthRows.filter((r) => allowed.has(r.monthKey));
  }, [data?.monthRows, periodMonthKeys]);

  const serverTotalRow = useMemo(() => {
    if (periodMonthKeys?.length) return null;
    return data?.totalRow ?? null;
  }, [data?.totalRow, periodMonthKeys]);

  const selectedVersionName =
    versions.find((v) => v.key === version)?.name ??
    data?.versionName ??
    "Live EAC";

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = FILTERS_CONFIG.filter(
    (f) =>
      visibleKeys.includes(f.key) && isFilterActive(f, appliedFilters[f.key]),
  );

  const isNoneActive = (fv) =>
    !visibleKeys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, fv[k]);
    });

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      visibleKeys.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadForecastSummaryMonthlyAPI(
        buildForecastSummaryDownloadFilters(effectiveFilters),
        version,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "forecast-monthly-summary.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Forecast Monthly Summary download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [effectiveFilters, version, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Forecast Monthly Summary</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <GlobalFilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isNoneActive(draftFilters)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isNoneActive(appliedFilters)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow="Forecast Monthly Summary"
          title="Monthly"
          accent="Revenue & Cost"
          badge="Live Forecast"
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {/* Version picker */}
          <div className={classes.filterRow}>
            <div className={classes.filterGroup}>
              <span className={classes.filterLabel}>Version</span>
              <select
                className={classes.filterSelect}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={versions.length === 0}
              >
                {versions.length === 0 && <option value="">—</option>}
                {versions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {activeTab === "forecast_summary" &&
            (loading ? (
              <Loading />
            ) : (
              <ForecastSummaryTable
                rows={rows}
                totalRow={serverTotalRow}
                version={selectedVersionName}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
